import React from "react";
import { CSSObject, Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { sidebarWidthExpanded } from "styles/constants";

import { FeatureType } from "components/paywall/paywallUtils";
import toast from "components/ui/toast/Toast";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useDowngrade } from "lib/hooks/api/subscription/useDowngrade";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { usePqlActioned } from "lib/hooks/api/users/usePqlActioned";
import { EventNames, useTracking } from "lib/tracking";
import { PqlActionType, SubscriptionEnum } from "lib/types/graphQLEnums";
import { useSupportChat } from "lib/utils/useSupportChat";
import { upgradeLink } from "./utils";

export function PlanActionButton({
  plan,
  currentPlan,
  triggerBookADemoForm,
  triggerDowngradeModal,
  scrollToPlanComparison,
  tableHeader = false,
  annualPlan = false,
  dataType,
}: {
  plan: SubscriptionEnum;
  currentPlan: SubscriptionEnum;
  triggerBookADemoForm: (value: boolean) => void;
  triggerDowngradeModal: (value: boolean) => void;
  scrollToPlanComparison?: () => void;
  tableHeader?: boolean;
  annualPlan?: boolean;
  dataType?: FeatureType;
}): JSX.Element {
  const { openSupportChat } = useSupportChat();

  const { mutate: mutateDowngrade } = useDowngrade();
  const { mutate: mutatePql } = usePqlActioned(
    undefined,
    dataType ? { "Data type": dataType } : {},
  );
  const isRelevancySearchNewUsersEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const { logEvent } = useTracking();
  const user = useAssertCurrentUser();

  const upgradeToBasic = () => {
    if (isRelevancySearchNewUsersEnabled) {
      triggerBookADemoForm(true);
      mutatePql({
        actionType: PqlActionType.BookADemoInApp,
        pageUrl: window.location.href,
        dataType,
      });
      return;
    }

    // This is on a <a href="..."> so we don't need to navigate to the page here
    mutatePql({
      actionType: PqlActionType.UpgradeToBasic,
      pageUrl: window.location.href,
      dataType,
    });
  };

  const upgradeToGrowthOrExpert = () => {
    triggerBookADemoForm(true);
    mutatePql({
      actionType: PqlActionType.BookADemoInApp,
      pageUrl: window.location.href,
      dataType,
    });
  };

  const upgrade = (newPlan: SubscriptionEnum, annual?: boolean) => {
    let context: {
      "Context source": string;
      "Current plan": SubscriptionEnum;
      "Requested plan": SubscriptionEnum;
      "Self Serve Payment"?: string;
      "Data type"?: FeatureType;
    } = {
      "Context source": tableHeader ? "Plan comparison table" : "Plan card",
      "Current plan": currentPlan,
      "Requested plan": newPlan,
    };

    if (dataType) {
      context["Data type"] = dataType;
    }

    if (newPlan === "basic") {
      context["Self Serve Payment"] = annual
        ? "Upgrade to basic - annual"
        : "Upgrade to basic - monthly";
    }

    logEvent(EventNames.planUpgradeClicked, context);

    if (newPlan === "growth" || newPlan === "expert") {
      upgradeToGrowthOrExpert();
    }
    if (newPlan === "basic") {
      upgradeToBasic();
    }
  };

  const downgradeBasicToFree = () => {
    openSupportChat("Downgrade to Free from Basic");
  };

  const downgradeFromGrowthOrExpert = (newPlan: SubscriptionEnum) => {
    mutateDowngrade(
      { input: { newPlan } },
      {
        onSuccess: () => {
          triggerDowngradeModal(true);
        },
        onError: () => {
          toast.error(
            "Failed to request subscription downgrade. Please contact your CSM instead, or try again later.",
          );
        },
      },
    );
  };

  const downgrade = (newPlan: SubscriptionEnum) => {
    logEvent(EventNames.planDowngradeClicked, {
      "Context source": tableHeader ? "Plan comparison table" : "Plan card",
      "Current plan": currentPlan,
      "Requested plan": newPlan,
    });

    if (currentPlan === "basic") {
      downgradeBasicToFree();
    } else {
      downgradeFromGrowthOrExpert(newPlan);
    }
  };

  if (currentPlan === plan) {
    if (scrollToPlanComparison) {
      return (
        <SubscriptionButton
          variant="outline"
          tableHeader={tableHeader}
          onClick={scrollToPlanComparison}
        >
          See features
        </SubscriptionButton>
      );
    }
    return <SubscriptionButton variant="disabled">Current plan</SubscriptionButton>;
  }
  if (plan === SubscriptionEnum.Freemium) {
    return (
      <SubscriptionButton
        variant="outline"
        tableHeader={tableHeader}
        onClick={() => downgrade(SubscriptionEnum.Freemium)}
      >
        Change plan
      </SubscriptionButton>
    );
  }
  if (plan === SubscriptionEnum.Basic) {
    if (currentPlan === SubscriptionEnum.Freemium) {
      if (isRelevancySearchNewUsersEnabled) {
        return (
          <SubscriptionButton
            variant="default"
            tableHeader={tableHeader}
            onClick={() => upgrade(SubscriptionEnum.Basic, annualPlan)}
          >
            Book a call
          </SubscriptionButton>
        );
      }

      return (
        <SubscriptionButtonLink
          variant="default"
          tableHeader={tableHeader}
          href={upgradeLink(annualPlan, user.email)}
          target="_blank"
          onClick={() => upgrade(SubscriptionEnum.Basic, annualPlan)}
        >
          Upgrade
        </SubscriptionButtonLink>
      );
    }
    return (
      <SubscriptionButton
        variant="outline"
        tableHeader={tableHeader}
        onClick={() => downgrade(SubscriptionEnum.Basic)}
      >
        Change plan
      </SubscriptionButton>
    );
  }
  if (plan === SubscriptionEnum.Growth) {
    if (currentPlan === SubscriptionEnum.Freemium || currentPlan === SubscriptionEnum.Basic) {
      return (
        <SubscriptionButton
          variant="default"
          tableHeader={tableHeader}
          onClick={() => upgrade(SubscriptionEnum.Growth)}
        >
          Book a call
        </SubscriptionButton>
      );
    }
    return (
      <SubscriptionButton
        variant="outline"
        tableHeader={tableHeader}
        onClick={() => downgrade(SubscriptionEnum.Growth)}
      >
        Change plan
      </SubscriptionButton>
    );
  }
  if (plan === SubscriptionEnum.Expert) {
    return (
      <SubscriptionButton
        variant="default"
        tableHeader={tableHeader}
        onClick={() => upgrade(SubscriptionEnum.Expert)}
      >
        Book a call
      </SubscriptionButton>
    );
  }
  return <></>;
}

interface ButtonProps {
  variant: "default" | "outline" | "disabled";
  tableHeader?: boolean;
}
interface ButtonStyle extends ButtonProps {
  theme: Theme;
}
const buttonStyle = ({ theme, variant, tableHeader }: ButtonStyle): CSSObject => ({
  minHeight: "32px",
  paddingLeft: "12px",
  paddingRight: "12px",
  borderRadius: "4px",
  borderStyle: "solid",
  cursor: "pointer",
  alignContent: "center",
  transition: "background-color 0.2s",
  fontWeight: "400",

  ...(tableHeader
    ? {
        width: "100%",
        fontSize: "14px",

        // Keep in sync with SimplePlanComparisonTable's .headerRow
        [`@media (max-width: calc(${sidebarWidthExpanded} + 680px))`]: {
          minHeight: "58px",
        },
      }
    : {
        width: "100%",
      }),

  ...(variant === "default"
    ? {
        backgroundColor: theme.colors.sysPrimaryDefault,
        color: theme.colors.sysTextWhite,
        borderWidth: "0px",
        borderColor: theme.colors.sysPrimaryDefault,

        "&:hover": {
          backgroundColor: theme.colors.sysPrimaryHover,
          color: theme.colors.sysTextWhite,
        },
      }
    : variant === "outline"
      ? {
          backgroundColor: theme.colors.sysBackgroundDefault,
          borderWidth: "1px",
          borderColor: theme.colors.sysBorderDecorative,

          "&:hover": {
            backgroundColor: theme.colors.sysBackgroundAlternative,
            color: theme.colors.sysTextDefault,
          },
        }
      : variant === "disabled"
        ? {
            backgroundColor: theme.colors.sysBackgroundInactive,
            color: theme.colors.sysTextInactive,
            borderWidth: "1px",
            borderColor: theme.colors.sysBorderDecorative,
            cursor: "not-allowed",

            "&:hover": {
              backgroundColor: theme.colors.sysBackgroundInactive,
              color: theme.colors.sysTextInactive,
            },
          }
        : {}),

  "&:focus": {
    outlineColor: theme.colors.sysPrimaryDefault,
    outlineOffset: "2px",
    outlineWidth: "2px",
    outlineStyle: "solid",
  },
});

const SubscriptionButton = styled.button<ButtonProps>(({ theme, variant, tableHeader }) =>
  buttonStyle({ theme, variant, tableHeader }),
);
const SubscriptionButtonLink = styled.a<ButtonProps>(({ theme, variant, tableHeader }) => ({
  ...buttonStyle({ theme, variant, tableHeader }),
  textDecoration: "none",
  textAlign: "center",
  width: "100%",
}));
